import React, { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  Alert,
  Box, Button, Card, CardContent, Chip, Grid, Paper, Typography
} from '@mui/material'
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Refresh from '@mui/icons-material/Refresh'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ArchiveIcon from '@mui/icons-material/Archive'

import {
  useGetJournalReconPeriodDetailsQuery,
  useListJournalReconReportsQuery
} from '../../../../api-services/financial/journalReconApi'
import { type ExportJournalDetails } from '../../../../api-schema-types/financial/JournalReconDomainDtos'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import CloseJournalReconPeriodDialog from './CloseJournalReconPeriodDialog'
import DownloadJournalReconReportButton from './DownloadJournalReconReportButton'
import DownloadJournalReconZipButton from './DownloadJournalReconZipButton'

const JournalReconPeriodPage: React.FC = () => {
  const params = useParams<{ startDate: string }>()
  const history = useHistory()
  const startDate = params.startDate

  const [closeDialogOpen, setCloseDialogOpen] = useState<boolean>(false)

  const getDetailsApiResult = useGetJournalReconPeriodDetailsQuery(startDate)
  const listReportsApiResult = useListJournalReconReportsQuery()

  const details: ExportJournalDetails[] = getDetailsApiResult.data ?? []
  const isClosed = details.length > 0 && details.every(d => d.processedAt !== null)
  const allUnprocessed = details.length > 0 && details.every(d => d.processedAt === null)
  const processedBy = details.find(d => d.processedByEmail !== null)?.processedByEmail ?? null
  const processedAt = details.find(d => d.processedAt !== null)?.processedAt ?? null
  const periodEndAt = details[0]?.periodEndAt ?? ''

  const goTo = (location: string): void => { history.push(location) }

  const columns: GridColDef[] = [
    { field: 'transactionTypeCode', headerName: 'Transaction Type', flex: 1 },
    { field: 'exportDate', headerName: 'Export Date', width: 150 },
    {
      field: 'processedAt',
      headerName: 'Processed At',
      width: 180,
      renderCell: (p: GridRenderCellParams) => p.row.processedAt ?? <em>Not processed</em>
    },
    { field: 'processedByEmail', headerName: 'Processed By', flex: 1 }
  ]

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button variant={'text'} type='button' onClick={() => { goTo('/app/finance-area/journal-recons') }}>
            <ArrowBackIcon style={{ color: '#444' }} />
          </Button>
          <Typography variant={'h4'}>Journal Period {startDate}{periodEndAt !== '' ? ` → ${periodEndAt}` : ''}</Typography>
          { details.length > 0 && (
            isClosed
              ? <Chip label='Closed' color='success' />
              : <Chip label='Open' color='warning' />
          )}
        </Box>
        <Button variant={'contained'} type='button' onClick={async () => { await getDetailsApiResult.refetch() }}>
          <Refresh />
        </Button>
      </Box>

      <Paper sx={{ p: 4 }}>
        { getDetailsApiResult.isLoading && <LoadingComponent /> }
        { getDetailsApiResult.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getDetailsApiResult.error)} /> }

        { getDetailsApiResult.isSuccess && details.length === 0 && (
          <Alert severity='info'>No export journals exist for this period.</Alert>
        )}

        { getDetailsApiResult.isSuccess && details.length > 0 && (
          <>
            { isClosed && processedAt !== null && (
              <Alert severity='success' sx={{ mb: 2 }}>
                Closed on { processedAt } by { processedBy }.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <h3>Journal Detail</h3>
                <DataGrid
                  getRowId={(row) => row.transactionTypeCode}
                  rows={details}
                  columns={columns}
                  autoHeight={true}
                  disableSelectionOnClick

                  pagination
                  pageSize={25}
                  rowsPerPageOptions={[25, 50, 100]}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant='outlined'>
                  <CardContent>
                    <Typography variant='h6' gutterBottom>
                      <LockOutlinedIcon sx={{ verticalAlign: 'middle', mr: 1 }} fontSize='small' />
                      Close Period
                    </Typography>
                    { allUnprocessed && (
                      <>
                        <Typography variant='body2' sx={{ mb: 2 }}>
                          Closing the period marks the journals processed and unlocks the CSV exports.
                          Your email address will be recorded against each journal.
                        </Typography>
                        <Button
                          variant='contained'
                          color='primary'
                          startIcon={<LockOutlinedIcon />}
                          onClick={() => { setCloseDialogOpen(true) }}
                        >
                          Close Period
                        </Button>
                      </>
                    )}
                    { !allUnprocessed && isClosed && (
                      <Typography variant='body2'>This period is already closed.</Typography>
                    )}
                    { !allUnprocessed && !isClosed && (
                      <Alert severity='warning'>
                        Some journals are processed but others are not — the period is in an inconsistent state.
                        Contact engineering before retrying.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Typography variant='h5' gutterBottom>
                <ArchiveIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Recon Exports
              </Typography>

              <Card variant='outlined' sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant='subtitle1' gutterBottom>Download all (ZIP)</Typography>
                  <Typography variant='body2' sx={{ mb: 2 }}>
                    Bundles all eight CSV files into a single ZIP archive.
                  </Typography>
                  <DownloadJournalReconZipButton
                    startDate={startDate}
                    disabled={false}
                  />
                </CardContent>
              </Card>

              <Card variant='outlined'>
                <CardContent>
                  <Typography variant='subtitle1' gutterBottom>Individual CSVs</Typography>
                  { listReportsApiResult.isLoading && <LoadingComponent /> }
                  { listReportsApiResult.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(listReportsApiResult.error)} /> }
                  { listReportsApiResult.isSuccess && (
                    <Grid container spacing={1}>
                      { listReportsApiResult.data.map((report) => (
                        <Grid item xs={12} sm={6} md={4} key={report.slug}>
                          <DownloadJournalReconReportButton
                            startDate={startDate}
                            report={report}
                            disabled={false}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Box>
          </>
        )}
      </Paper>

      { closeDialogOpen && (
        <CloseJournalReconPeriodDialog
          startDate={startDate}
          onCloseDialog={() => { setCloseDialogOpen(false) }}
          onSuccess={async () => {
            setCloseDialogOpen(false)
            await getDetailsApiResult.refetch()
          }}
        />
      )}
    </>
  )
}

export default JournalReconPeriodPage
